import axios from 'axios';

const server = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: { 'Content-Type': 'application/json' }
});

const tableSessionsBasePath = '/table-sessions';

export class TableSessionsFetchAPI {
    static async getPendingSessions({ branchoffice_code, status, page, limit }, token) {
        const params = new URLSearchParams();
        if (branchoffice_code) params.append('branchoffice_code', branchoffice_code);
        if (status) params.append('status', status);
        if (page) params.append('page', page);
        if (limit) params.append('limit', limit);

        const res = await server(`${tableSessionsBasePath}/pending?${params.toString()}`, {
            method: 'get',
            headers: { 'x-access-token': token },
        });
        return res;
    }

    static async getPendingKitchenPrints({ branchoffice_code }, token) {
        const params = new URLSearchParams();
        if (branchoffice_code) params.append('branchoffice_code', branchoffice_code);

        const res = await server(`${tableSessionsBasePath}/pending-kitchen-prints?${params.toString()}`, {
            method: 'get',
            headers: { 'x-access-token': token },
        });
        return res;
    }

    static async getActiveSessionByTable(table_id, token) {
        const res = await server(`${tableSessionsBasePath}/table/${table_id}/active`, {
            method: 'get',
            headers: { 'x-access-token': token },
        });
        return res;
    }

    static async approveSession(session_uuid, data, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/approve`, {
            method: 'post',
            headers: { 'x-access-token': token },
            data,
        });
        return res;
    }

    static async rejectSession(session_uuid, data, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/reject`, {
            method: 'post',
            headers: { 'x-access-token': token },
            data,
        });
        return res;
    }

    static async closeSession(session_uuid, data, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/close`, {
            method: 'post',
            headers: { 'x-access-token': token },
            data,
        });
        return res;
    }

    // POS "Mesas": el cliente se cambio de mesa - mueve la sesion (pedido, pagos y QR incluidos).
    static async moveTable(session_uuid, target_table_id, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/move-table`, {
            method: 'post',
            headers: { 'x-access-token': token },
            data: { target_table_id },
        });
        return res;
    }

    // POS "Mesas"/"Precuentas": lets a waiter enable the ecommerce customer's Pagar button.
    static async authorizePayment(session_uuid, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/authorize-payment`, {
            method: 'post',
            headers: { 'x-access-token': token },
        });
        return res;
    }

    // POS "Mesas" - "Generar QR para pedir y pagar": mints a fresh single-use prepay QR for a table,
    // invalidating whatever was still active for it. Only one is ever valid per table at a time.
    static async generatePrepayQr(table_id, token) {
        const res = await server(`${tableSessionsBasePath}/admin/table/${table_id}/prepay-qr`, {
            method: 'post',
            headers: { 'x-access-token': token },
        });
        return res;
    }

    // POS "Mesas" - "Generar QR de mesa" (pospago): fetches the table's persistent public code,
    // lazily creating and enabling it on first call. Unlike the prepay QR, this one stays valid
    // across scans until it's explicitly regenerated or disabled.
    static async getTableQr(table_id, token) {
        const res = await server(`${tableSessionsBasePath}/admin/table/${table_id}/qr`, {
            method: 'get',
            headers: { 'x-access-token': token },
        });
        return res;
    }

    // Enables/disables the pospago table QR without changing its code.
    static async setTableAccess(table_id, enabled, token) {
        const res = await server(`${tableSessionsBasePath}/admin/table/${table_id}/access`, {
            method: 'patch',
            headers: { 'x-access-token': token },
            data: { enabled },
        });
        return res;
    }

    // Rotates the pospago table's public code, invalidating whatever QR was printed before.
    static async regenerateTableCode(table_id, token) {
        const res = await server(`${tableSessionsBasePath}/admin/table/${table_id}/regenerate-code`, {
            method: 'post',
            headers: { 'x-access-token': token },
        });
        return res;
    }

    // Staff-only: forces the closure/invoice of a pre-pago account (Fase 3/5). Unlike the customer's
    // own "Finalizar cuenta", this succeeds even with a pending balance - the shortfall is recorded
    // automatically as a manual cash entry by the backend (see
    // services/tableSessions.js#forceFinalizeTableAccount), no payment-method choice needed here.
    static async forceFinalizeAccount(session_uuid, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/prepay/force-finalize`, {
            method: 'post',
            headers: { 'x-access-token': token },
        });
        return res;
    }

    // "Cuenta pre-pago": antes de forzar el cierre, el mesero puede en vez de eso cobrar de verdad los
    // productos pendientes (abre el mismo Centro de Pagos de "Cobrar en terminal") o eliminarlos sin
    // cobrar. Ver services/tableSessionPayments.js en el backend.
    static async staffClaimPendingLines(session_uuid, line_uuids, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/prepay/order/lines/staff-claim`, {
            method: 'post',
            headers: { 'x-access-token': token },
            data: { line_uuids },
        });
        return res;
    }

    static async cancelPendingLines(session_uuid, line_uuids, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/prepay/order/lines/cancel`, {
            method: 'post',
            headers: { 'x-access-token': token },
            data: { line_uuids },
        });
        return res;
    }

    // "Personalizar factura" al cerrar la cuenta: nombre/RUC/DV con los que se emitira la factura final
    // (los mismos que puede cargar el cliente desde su carrito). Todo vacio quita la personalizacion.
    static async setInvoiceData(session_uuid, data, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/invoice-data`, {
            method: 'patch',
            headers: { 'x-access-token': token },
            data,
        });
        return res;
    }

    // Anula la cuenta pre-pago completa (sin factura) cuando no se ha cobrado nada todavia - la
    // alternativa a ir eliminando/cobrando linea por linea cuando de plano no hay nada que facturar.
    static async voidPrepayAccount(session_uuid, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/prepay/void`, {
            method: 'post',
            headers: { 'x-access-token': token },
        });
        return res;
    }

    // Solicitudes de descuento en espera de la sesion (las pide el cliente e-commerce sobre uno de sus
    // productos) + los descuentos porcentuales que la sucursal puede conceder.
    static async getDiscountRequests(session_uuid, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/discount-requests`, {
            method: 'get',
            headers: { 'x-access-token': token },
        });
        return res;
    }

    // data: { action: 'APPROVE', discount_id } | { action: 'REJECT', note? }
    static async resolveDiscountRequest(session_uuid, line_uuid, data, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/discount-requests/${line_uuid}`, {
            method: 'post',
            headers: { 'x-access-token': token },
            data,
        });
        return res;
    }

    // "Pagar con POS": confirms a physical-terminal charge (BAC/Banesco/KinPOS/manual) for a claim
    // the ecommerce customer raised on a pre-pago table - marks only those lines PAID, the account
    // stays open. paymentDetails must include calculation_id (from the tableSessionPhysicalPaymentRequested
    // socket payload) plus paymethod_id/paymenthod_integration_id/gateway_name/gateway_reference.
    static async confirmPhysicalPayment(session_uuid, payment_attempt_id, paymentDetails, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/prepay/order/lines/${payment_attempt_id}/staff-confirm`, {
            method: 'post',
            headers: { 'x-access-token': token },
            data: paymentDetails,
        });
        return res;
    }

    // Called when the physical charge fails/is cancelled on the terminal, so the lines become
    // payable again immediately instead of waiting out the backend's claim TTL.
    static async releasePhysicalPayment(session_uuid, payment_attempt_id, reason, token) {
        const res = await server(`${tableSessionsBasePath}/${session_uuid}/prepay/order/lines/${payment_attempt_id}/staff-release`, {
            method: 'post',
            headers: { 'x-access-token': token },
            data: reason ? { reason } : undefined,
        });
        return res;
    }
}
